import React from "react";

/**
 * Red de seguridad de toda la aplicación.
 *
 * Sin esto, cualquier excepción en un render o en un efecto desmonta el árbol
 * completo y el candidato se queda mirando una página en blanco, sin saber si
 * el enlace está roto, si es su navegador o si la entrevista ya no existe.
 *
 * Va sin styled-components ni i18n a propósito: tiene que poder pintarse aunque
 * lo que haya fallado sea justamente una de esas piezas.
 */

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

const isEnglish = () =>
  typeof navigator !== "undefined" &&
  (navigator.language || "").toLowerCase().startsWith("en");

const COPY = {
  es: {
    title: "No pudimos cargar la entrevista",
    body: "Algo falló al abrir esta página en tu navegador. Copia el enlace y ábrelo directamente en Chrome o Safari, desde una computadora si es posible.",
    hint: "Si lo abriste desde el correo, LinkedIn o WhatsApp, esa es la causa más común.",
    button: "Reintentar",
  },
  en: {
    title: "We couldn't load the interview",
    body: "Something failed while opening this page in your browser. Copy the link and open it directly in Chrome or Safari, preferably on a computer.",
    hint: "If you opened it from your email, LinkedIn or WhatsApp, that is the most common cause.",
    button: "Try again",
  },
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "#232323",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    maxWidth: 480,
    width: "100%",
    background: "#ffffff",
    borderRadius: 16,
    padding: "36px 32px",
    textAlign: "center",
    boxShadow: "0 18px 48px rgba(35, 35, 35, 0.24)",
  },
  title: { fontSize: 22, fontWeight: 600, color: "#232323", margin: "0 0 12px" },
  body: { fontSize: 16, color: "#5a5a5a", lineHeight: 1.6, margin: "0 0 12px" },
  hint: { fontSize: 14, color: "#8a8a8a", lineHeight: 1.5, margin: "0 0 24px" },
  button: {
    background: "#D40000",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    padding: "14px 32px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
  },
};

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || "" };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Queda en la consola del candidato para poder pedírsela en soporte.
    console.error("[interview] render abortado:", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const copy = isEnglish() ? COPY.en : COPY.es;

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h1 style={styles.title}>{copy.title}</h1>
          <p style={styles.body}>{copy.body}</p>
          <p style={styles.hint}>{copy.hint}</p>
          <button
            style={styles.button}
            onClick={() => window.location.reload()}
          >
            {copy.button}
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
